import { ActionListItem } from "../ActionList/ActionListItem";
import { MoreInfo} from "../MoreInfo/MoreInfo";
import { Toolbar} from "../Toolbar/Toolbar";
import {
	ensureNotNil,
	isArray,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import styles from "./PageContentTitle.module.less";
import {
	ActionBarItemAlignment,
	ActionGroup,
	ActionKind,
	BusyIndicatorAction,
	MultiSelectOptionAction,
	DividerAction,
	reduceToAvailableActions,
	RegularAction,
	resolveActionTitle,
	SelectOptionAction
} from "client_legacy/ndaw/actions";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import { never } from "client_legacy/ndaw/errors";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { Tag } from "../Tag/Tag";

export type PageContentTitleProps<TActionContext> = {
	title: string | ReactElement;
	onMoreInfo?: () => void;
	tagContent?: string;
} & (
		{
			actions?: undefined;
			actionContext?: undefined;
		}
		|
		{
			actions: readonly ToolbarAction<TActionContext>[];
			actionContext: TActionContext;
		}
	);

export const PageContentTitle = <TActionContext,>(props: PageContentTitleProps<TActionContext>): ReactElement => {
	const {
		title,
		onMoreInfo,
		tagContent
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	return (
		<div
			{...setTrackingId("page-content-title")}
			className={styles.root}
		>
			<h2 className={styles.titleAndSuffix}>
				<span className={styles.title}>
					{title}
				</span>
				{tagContent && (
					<div className={styles.tagContainer}>
						<Tag text={tagContent} />
					</div>
				)}
				{onMoreInfo && (
					<MoreInfo onClick={onMoreInfo} trackingId="more-info" />
				)}

			</h2>

			{!isMobile && isNotNil(props.actions) && (
				<ActionBar<TActionContext>
					actions={props.actions}
					actionContext={props.actionContext!}
				/>
			)}
		</div>
	);
};
PageContentTitle.displayName = "PageContentTitle";

export type ToolbarActionGroup = ActionGroup & {
	isPrimary?: boolean;
};

export type ToolbarRegularAction<TContext> = RegularAction<TContext> & {
	isPrimary?: boolean;
};

export type ToolbarAction<TContext> = (
	| ToolbarRegularAction<TContext>
	| SelectOptionAction<TContext>
	| MultiSelectOptionAction<TContext>
	| DividerAction<TContext>
	| BusyIndicatorAction<TContext>
) & {
	group?: ToolbarActionGroup;
};

type ActionBarProps<TContext> = {
	actions: readonly ToolbarAction<TContext>[];
	actionContext: TContext;
};

const ActionBar = <TContext,>(props: ActionBarProps<TContext>): ReactElement | null => {
	const {
		actions,
		actionContext
	} = props;

	const [maxContentWidth, setMaxContentWidth] = useState<number | undefined>();

	const actionsToRender = useMemo(
		(): readonly ToolbarAction<TContext>[] => {
			return reduceToAvailableActions(actions, actionContext);
		},
		[actionContext, actions]
	);

	const actionsByGroup = useMemo(
		(): Partial<Record<string, readonly ToolbarAction<TContext>[]>> => groupBy(
			actionsToRender,
			item => item.group?.name || "__NONE__"
		),
		[actionsToRender]
	);

	const [leftSideActions, rightSideActions] = useMemo(
		(): [left: readonly ReactElement[], right: readonly ReactElement[]] => {
			const actionsByAlignment: Partial<Record<ActionBarItemAlignment, readonly ToolbarAction<TContext>[]>> = groupBy(
				actionsToRender,
				(x) => x.align ?? ActionBarItemAlignment.left
			);

			const leftSideActions: readonly ReactElement[] = mapActions(
				actionsByGroup,
				actionsByAlignment[ActionBarItemAlignment.left] ?? [],
				actionContext
			);

			const rightSideActions: readonly ReactElement[] = mapActions(
				actionsByGroup,
				actionsByAlignment[ActionBarItemAlignment.right] ?? [],
				actionContext
			);

			return [leftSideActions, rightSideActions];
		},
		[
			actionsToRender,
			actionsByGroup,
			actionContext
		]
	);

	if (leftSideActions.length + rightSideActions.length === 0) {
		return null;
	}

	return (
		<div
			className={styles.actions}
			style={{ flexBasis: maxContentWidth }}
		>
			<Toolbar
				trackingId="actions"
				buttonSize="large"
				onMaxContentWidthChange={setMaxContentWidth}
				horizontalAlignment="right"
			>
				{leftSideActions}
				{rightSideActions}
			</Toolbar>
		</div>
	);
};
ActionBar.displayName = "ActionBar";

function mapActions<TContext>(
	actionsByGroup: Partial<Record<string, readonly ToolbarAction<TContext>[]>>,
	actionList: readonly ToolbarAction<TContext>[],
	context: TContext
): readonly ReactElement[] {
	const actionGroupList: (ToolbarAction<TContext> | readonly ToolbarAction<TContext>[])[] = [];
	const usedGroups = new Set();

	for (const action of actionList) {
		if (!action.group) {
			actionGroupList.push(action);

			continue;
		}

		if (!usedGroups.has(action.group.name)) {
			const actionGroup: readonly ToolbarAction<TContext>[] = ensureNotNil(
				actionsByGroup[action.group.name]
			);

			actionGroupList.push(actionGroup);
			usedGroups.add(action.group.name);
		}
	}

	const result: ReactElement[] = mapGroupsAndActions(actionGroupList, context);

	return result;
}

function mapGroupsAndActions<TContext>(
	actions: readonly (ToolbarAction<TContext> | readonly ToolbarAction<TContext>[])[],
	context: TContext
): ReactElement[] {
	const result: ReactElement[] = [];

	for (const actionOrActions of actions) {
		if (isArray(actionOrActions)) {
			result.push(
				mapGroupAction(actionOrActions, context)
			);
			continue;
		} else if (actionOrActions.group !== undefined) {
			throw new Error("child actions are not supported at the root");
		} else {
			result.push(
				mapTopAction(actionOrActions, context)
			);
		}
	}

	return result;
}

function mapGroupAction<TContext>(
	actions: readonly ToolbarAction<TContext>[],
	context: TContext
): ReactElement {
	const [firstAction] = actions;
	const group: ToolbarActionGroup = ensureNotNil(firstAction.group);

	switch (group.kind) {
		case "alternative":
			if (actions.length === 1) {
				return mapTopAction(firstAction, context);
			} else {
				return (
					<Toolbar.MenuButton
						kind={group.isPrimary ? "primary" : "secondary"}
						key={group.name}
						icon={group.icon}
						text={group.title}
						trackingId={group.name}
						rightIcon="chevron-down"
						responsiveBehavior={
							group.isPrimary
								? "always-visible"
								: undefined
						}
					>
						{actions.map((x) => mapChildAction(x, context))}
					</Toolbar.MenuButton>
				);
			}
		case "regular":
			return (
				<Toolbar.MenuButton
					kind={group.isPrimary ? "primary" : "secondary"}
					key={group.name}
					icon={group.icon}
					text={group.title}
					trackingId={group.name}
					rightIcon="chevron-down"
					responsiveBehavior={
						group.isPrimary
							? "always-visible"
							: undefined
					}
				>
					{actions.map((x) => mapChildAction(x, context))}
				</Toolbar.MenuButton>
			);
		case "virtual":
			return (
				<>
					{actions.map((x) => mapTopAction(x, context))}
				</>
			);

		default:
			never(group);
	}
}

function mapTopAction<TContext>(
	action: ToolbarAction<TContext>,
	context: TContext
): ReactElement {

	const text: string = isNotNil(action.title)
		? resolveActionTitle(action.title, context)
		: "";

	switch (action.kind) {
		case ActionKind.Regular:
			return (
				<Toolbar.Button
					kind={action.isPrimary ? "primary" : "secondary"}
					key={action.name}
					icon={action.icon}
					text={text}
					onClick={() => action.execute(context)}
					trackingId={action.name}
					isDisabled={action.isDisabled?.(context)}
					responsiveBehavior={
						action.isPrimary
							? "always-visible"
							: undefined
					}
				/>
			);
		case ActionKind.Divider:
			return (
				<Toolbar.Divider
					key={action.name}
				/>
			);
		case ActionKind.BusyIndicator:
		case ActionKind.SelectOption:
		case ActionKind.MultiSelectOption:
			throw new Error(`'${action.kind}' is only supported as child action`);
		default:
			never(action);
	}
}

function mapChildAction<TContext>(
	action: ToolbarAction<TContext>,
	context: TContext
): ReactElement {
	if (action.kind === ActionKind.Regular) {
		return (
			<ActionListItem
				key={action.name}
				trackingId={action.name}
				icon={action.icon}
				text={resolveActionTitle(action.title, context)}
				onClick={() => action.execute(context)}
				isDisabled={action.isDisabled?.(context)}
			/>
		);
	}

	return actionComponentFactory(action, context);
}
